import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import type { Hybrid } from './hybrid-card';

/**
 * Applies the hybrid an entry point handed over in `?product_profile=`.
 *
 * Product exploration links straight into the wizard with the hybrid the user was already
 * looking at. The selection is seeded once, and only when that hybrid is actually among the
 * ones offered for this field — the list is ranked per environment, so a handed-over id can
 * be absent, and selecting something the user cannot see on the step would be worse than
 * ignoring it. An existing selection always wins: going back to the step must not re-seed
 * over what the user picked.
 *
 * Shared by the hybrid and product steps so the two cannot drift.
 */
export const usePreselectedProduct = ({ offered, selectedIds }: { offered: Hybrid[]; selectedIds: number[] }) => {
    const dispatch = useDispatch();
    const { search } = useLocation();
    const applied = useRef(false);
    const preselectedProductId = Number(new URLSearchParams(search).get('product_profile')) || undefined;

    useEffect(() => {
        if (applied.current || !preselectedProductId || selectedIds.length) return;
        if (!offered.some(({ id }) => id === preselectedProductId)) return;
        applied.current = true;
        dispatch(updateActiveRecommendationCropConfig({ key: 'products', value: [preselectedProductId] }));
    }, [preselectedProductId, selectedIds.length, offered, dispatch]);
};
